import React, { memo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  Cloud,
  CheckCircle2,
  FileText,
  AlertCircle,
  Eye,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { ACCEPT_FILES, validateFile } from "../../utils/fileValidation";

const getFileUrl = (file) => {
  if (!file) return null;
  if (typeof file === "string") return file;
  return file.url || file.previewUrl || null;
};

const getFileName = (file, fallback = "document") => {
  if (!file) return fallback;
  const raw = typeof file === "string" ? file : file.name || file.url;
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw).split("/").pop() || fallback;
  } catch {
    return raw.split("/").pop() || fallback;
  }
};

const DocumentUploadForm = memo(
  ({
    documentId,
    documentName,
    condition,
    isRequired = false,
    uploadType = "single",
    onUpload,
    onRemove,
    uploadedFiles = {},
    readOnly = false,
    onPreview,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [uploadError, setUploadError] = useState(null);

    const handleFileSelect = async (event, side = null) => {
      setUploadError(null);
      const selected = Array.from(event.target.files);
      event.target.value = "";
      if (selected.length === 0) return;

      const results = await Promise.all(
        selected.map(async (file) => ({
          file,
          error: await validateFile(file),
        })),
      );

      const valid = results.filter((r) => !r.error).map((r) => r.file);
      const errors = results.filter((r) => r.error).map((r) => r.error);

      if (errors.length) setUploadError(errors.join(" "));
      if (valid.length) onUpload(documentId, valid, side);
    };

    const handleFileRemove = (side, index = null) => {
      setUploadError(null);
      onRemove?.(documentId, side, index);
    };

    const getConditionStyles = (cond) => {
      const styles = {
        Mandatory: { bg: "#fee2e2", text: "#dc2626", label: "Mandatory" },
        Optional: { bg: "#fef3c7", text: "#f59e0b", label: "Optional" },
      };
      return styles[cond] || styles.Mandatory;
    };

    const conditionStyle = getConditionStyles(condition);
    const files = uploadedFiles[documentId] || {};

    const isFileAvailable = (file) =>
      Boolean(file && (typeof file === "string" || file.name || file.url));

    const sides =
      uploadType === "both"
        ? ["front", "back"]
        : uploadType === "multiple"
          ? ["multiple"]
          : ["single"];

    const allUploaded = sides.every((side) => {
      if (side === "multiple") {
        return Array.isArray(files.multiple) && files.multiple.length > 0;
      }
      return isFileAvailable(files[side]);
    });

    return (
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: { xs: "8px", sm: "12px" },
          overflow: "hidden",
          backgroundColor: "#fff",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "#d1d5db",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 2 },
            p: { xs: 1.5, sm: 2, md: 2.5 },
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
              }}
            >
              <Cloud
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  color: "#2563eb",
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "14px", sm: "16px", md: "16px" },
                  color: "#1f2937",
                  wordBreak: "break-word",
                }}
              >
                {documentName}
              </Typography>
            </Box>

            {uploadType === "both" && (
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  color: "#6b7280",
                  fontWeight: 500,
                  ml: { xs: 3, sm: 4.5 },
                }}
              >
                Front & Back required
              </Typography>
            )}
            {uploadType === "multiple" && (
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  color: "#6b7280",
                  fontWeight: 500,
                  ml: { xs: 3, sm: 4.5 },
                }}
              >
                Multiple files allowed
              </Typography>
            )}

            {readOnly && (
              <Box sx={{ mt: 1.5 }}>
                {uploadType === "both" ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: { xs: 1.5, sm: 2 },
                    }}
                  >
                    {["front", "back"].map((side) => {
                      const file = files[side];
                      if (!file) return null;
                      return (
                        <DocumentReadOnlySlot
                          key={side}
                          label={side === "front" ? "Front" : "Back"}
                          file={file}
                          onPreview={onPreview}
                        />
                      );
                    })}
                  </Box>
                ) : uploadType === "multiple" ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {(files.multiple || []).map((file, index) => (
                      <DocumentReadOnlySlot
                        key={`${documentId}-${index}`}
                        file={file}
                        onPreview={onPreview}
                      />
                    ))}
                  </Box>
                ) : (
                  <DocumentReadOnlySlot
                    file={files.single}
                    onPreview={onPreview}
                  />
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.5, sm: 1 },
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-end" },
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.75 },
                borderRadius: "6px",
                backgroundColor: conditionStyle.bg,
                color: conditionStyle.text,
                fontSize: { xs: "10px", sm: "12px" },
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {conditionStyle.label}
            </Box>

            {allUploaded && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.25, sm: 0.5 },
                  color: "#10b981",
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.25, sm: 0.5 },
                  borderRadius: "6px",
                  backgroundColor: "#f0fdf4",
                  whiteSpace: "nowrap",
                }}
              >
                <CheckCircle2
                  style={{ fontSize: isMobile ? "14px" : "16px" }}
                />
                <Typography
                  sx={{ fontSize: { xs: "10px", sm: "12px" }, fontWeight: 600 }}
                >
                  Done
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {!readOnly && (
          <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
            {uploadError && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <AlertCircle
                  style={{
                    fontSize: "16px",
                    color: "#dc2626",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: "11px", sm: "12px" },
                    color: "#991b1b",
                    fontWeight: 500,
                  }}
                >
                  {uploadError}
                </Typography>
              </Box>
            )}

            {uploadType === "both" ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                {["front", "back"].map((side) => (
                  <DocumentUploadSlot
                    key={side}
                    side={side}
                    label={side === "front" ? "Front" : "Back"}
                    file={files[side]}
                    onUpload={handleFileSelect}
                    onRemove={handleFileRemove}
                    onPreview={onPreview}
                    isUploaded={!!files[side]}
                    isMobile={isMobile}
                    isRequired={isRequired && !files[side]}
                  />
                ))}
              </Box>
            ) : uploadType === "multiple" ? (
              <DocumentMultipleUploadSlot
                files={files.multiple || []}
                onUpload={handleFileSelect}
                onRemove={handleFileRemove}
                onPreview={onPreview}
                isMobile={isMobile}
                isRequired={isRequired}
              />
            ) : (
              <DocumentUploadSlot
                side="single"
                label="Upload"
                file={files.single}
                onUpload={handleFileSelect}
                onRemove={handleFileRemove}
                onPreview={onPreview}
                isUploaded={!!files.single}
                isMobile={isMobile}
                isRequired={isRequired && !files.single}
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
);

const SlotAction = ({ icon: Icon, label, color, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.4,
      cursor: "pointer",
      "&:hover": { opacity: 0.75 },
    }}
  >
    <Icon size={12} style={{ color }} />
    {label && (
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color }}>
        {label}
      </Typography>
    )}
  </Box>
);

const DocumentUploadSlot = ({
  side,
  label,
  file,
  onUpload,
  onRemove,
  onPreview,
  isUploaded,
  isMobile,
  isRequired,
}) => {
  const inputRef = useRef(null);
  const fileUrl = file ? getFileUrl(file) : null;
  const fileName = file ? getFileName(file, label) : null;

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handlePreview = (event) => {
    event.stopPropagation();

    if (!fileUrl || !onPreview) {
      return;
    }

    onPreview(fileUrl, fileName);
  };

  const handleReplace = (event) => {
    event.stopPropagation();
    inputRef.current?.click();
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    onRemove?.(side);
  };

  return (
    <Box>
      <Box
        onClick={handleClick}
        sx={{
          border: `2px dashed ${isRequired ? "#dc2626" : "#d1d5db"}`,
          borderRadius: { xs: "6px", sm: "8px" },
          p: { xs: 2, sm: 2.5, md: 3 },
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isUploaded ? "#f0fdf4" : "#f9fafb",
          transition: "all 0.3s ease",
          "&:hover": { borderColor: "#2563eb", backgroundColor: "#eff6ff" },
          position: "relative",
          minHeight: { xs: "120px", sm: "140px", md: "160px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <input
          ref={inputRef}
          hidden
          type="file"
          accept={ACCEPT_FILES}
          onChange={(e) => onUpload(e, side)}
        />

        {file?.name || fileName ? (
          <>
            <CheckCircle2
              style={{ fontSize: isMobile ? "24px" : "32px", color: "#10b981" }}
            />
            <Typography
              sx={{
                mt: 1,
                fontWeight: 600,
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {isMobile
                ? fileName.length > 15
                  ? fileName.substring(0, 12) + "..."
                  : fileName
                : fileName}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                color: "#6b7280",
                mt: 0.5,
              }}
            >
              {file?.size ? (file.size / 1024 / 1024).toFixed(2) : "0.00"} MB
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
              {fileUrl && (
                <SlotAction
                  icon={Eye}
                  label="Preview"
                  color="#2563eb"
                  onClick={handlePreview}
                />
              )}
              <SlotAction
                icon={RefreshCw}
                label="Replace"
                color="#6b7280"
                onClick={handleReplace}
              />
              <SlotAction
                icon={Trash2}
                label="Remove"
                color="#dc2626"
                onClick={handleRemove}
              />
            </Box>
          </>
        ) : (
          <>
            <Cloud
              style={{
                fontSize: isMobile ? "24px" : "32px",
                color: "#9ca3af",
                margin: "0 auto",
              }}
            />
            <Typography
              sx={{
                mt: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                color: "#1f2937",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                color: "#6b7280",
                mt: { xs: 0.25, sm: 0.5 },
              }}
            >
              PNG, JPG, PDF
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "9px", sm: "11px" },
                color: "#9ca3af",
                mt: 0.25,
              }}
            >
              up to 10MB
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const DocumentMultipleUploadSlot = ({
  files,
  onUpload,
  onRemove,
  onPreview,
  isMobile,
  isRequired,
}) => {
  const inputRef = useRef(null);

  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `2px dashed ${isRequired ? "#dc2626" : "#d1d5db"}`,
          borderRadius: { xs: "6px", sm: "8px" },
          p: { xs: 2, sm: 2.5, md: 3 },
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: files.length > 0 ? "#f0fdf4" : "#f9fafb",
          transition: "all 0.3s ease",
          "&:hover": { borderColor: "#2563eb", backgroundColor: "#eff6ff" },
          minHeight: { xs: "120px", sm: "140px", md: "160px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <input
          ref={inputRef}
          hidden
          type="file"
          multiple
          accept="image/*,application/pdf,.pdf"
          onChange={(e) => onUpload(e, "multiple")}
        />

        {files.length > 0 ? (
          <>
            <CheckCircle2
              style={{
                fontSize: isMobile ? "24px" : "32px",
                color: "#10b981",
                margin: "0 auto",
              }}
            />
            <Typography
              sx={{
                mt: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                color: "#1f2937",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {files.length} file{files.length !== 1 ? "s" : ""} uploaded
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                color: "#6b7280",
                mt: { xs: 0.25, sm: 0.5 },
              }}
            >
              Click to add more
            </Typography>
          </>
        ) : (
          <>
            <Cloud
              style={{
                fontSize: isMobile ? "24px" : "32px",
                color: "#9ca3af",
                margin: "0 auto",
              }}
            />
            <Typography
              sx={{
                mt: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                color: "#1f2937",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {isMobile ? "Upload files" : "Click to upload or drag and drop"}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                color: "#6b7280",
                mt: { xs: 0.25, sm: 0.5 },
              }}
            >
              PNG, JPG, PDF
            </Typography>
          </>
        )}
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Typography
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              fontWeight: 600,
              color: "#6b7280",
              mb: { xs: 0.75, sm: 1 },
            }}
          >
            Files ({files.length}):
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 0.75, sm: 1 },
            }}
          >
            {files.map((file, idx) => {
              const fileUrl = getFileUrl(file);
              const fileName = getFileName(file);

              const handlePreview = (e) => {
                e.stopPropagation();
                if (fileUrl && onPreview) onPreview(fileUrl, fileName);
              };

              const handleRemove = (e) => {
                e.stopPropagation();
                onRemove?.("multiple", idx);
              };

              return (
                <Box
                  key={idx}
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    backgroundColor: "#f0fdf4",
                    borderRadius: "8px",
                    border: "1px solid #d1fae5",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Box
                    onClick={fileUrl ? handlePreview : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.75, sm: 1 },
                      flex: 1,
                      minWidth: 0,
                      cursor: fileUrl ? "pointer" : "default",
                    }}
                  >
                    <Box
                      sx={{
                        p: { xs: 0.5, sm: 0.75 },
                        borderRadius: "6px",
                        backgroundColor: "#ecfdf5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FileText
                        style={{
                          fontSize: isMobile ? "14px" : "16px",
                          color: "#10b981",
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1f2937",
                          fontSize: { xs: "11px", sm: "13px" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isMobile
                          ? fileName.length > 20
                            ? fileName.substring(0, 17) + "..."
                            : fileName
                          : fileName}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "10px", sm: "12px" },
                        color: "#6b7280",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file?.size
                        ? (file.size / 1024 / 1024).toFixed(2)
                        : "0.00"}{" "}
                      MB
                    </Typography>
                    {fileUrl && (
                      <SlotAction
                        icon={Eye}
                        label={isMobile ? "" : "Preview"}
                        color="#2563eb"
                        onClick={handlePreview}
                      />
                    )}

                    <SlotAction
                      icon={Trash2}
                      label={isMobile ? "" : "Remove"}
                      color="#dc2626"
                      onClick={handleRemove}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DocumentReadOnlySlot = ({ file, label, onPreview }) => {
  if (!file) return null;

  const fileUrl = getFileUrl(file);
  const fileName = getFileName(file, label || "document");

  const handleClick = () => {
    if (fileUrl && onPreview) onPreview(fileUrl, fileName);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        border: "1px solid #d1fae5",
        borderRadius: "8px",
        p: 2,
        backgroundColor: "#f0fdf4",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        cursor: fileUrl ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": fileUrl
          ? { borderColor: "#10b981", backgroundColor: "#ecfdf5" }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: "6px",
            backgroundColor: "#ecfdf5",
            flexShrink: 0,
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#059669" }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {label && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "#6b7280",
                fontWeight: 600,
                mb: 0.25,
              }}
            >
              {label}
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#1f2937",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName}
          </Typography>

          <Typography sx={{ fontSize: "11px", color: "#059669", mt: 0.25 }}>
            Uploaded · Click to preview
          </Typography>
        </Box>
      </Box>

      {fileUrl && (
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#2563eb",
            flexShrink: 0,
          }}
        >
          View
        </Typography>
      )}
    </Box>
  );
};

export default DocumentUploadForm;
